import {useEffect, useState} from "react";
import {useTelegram} from "../hooks/useTelegram";
import {checkAccessWithResponse} from "../api/knowledge.ts";
import {
    Container,
    Typography,
    Paper,
    Box,
    CircularProgress,
    Alert,
    Divider,
    Card,
    CardContent,
    CardHeader,
    Avatar
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ErrorIcon from '@mui/icons-material/Error';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

interface InitResponse {
    ok: boolean;
    error?: string;
    user?: {
        id: number;
        username: string;
    };
    data?: {
        id: number;
        title: string;
        content: string;
    };
}

export const KnowledgeContentPage = () => {
    const {tg, initData, user: telegramUser} = useTelegram();

    const [content, setContent] = useState<InitResponse["data"] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const urlParams = new URLSearchParams(window.location.search);
    const contentId = parseInt(urlParams.get("content_id") || "0", 10);
    const tgId = urlParams.get("tg_id") || "NONE";

    useEffect(() => {
        tg?.expand();

        const initRequest = async () => {
            setIsLoading(true);

            if (!initData) {
                setError("Telegram WebApp не надав дані для авторизації");
                setIsLoading(false);
                return;
            }

            if (!contentId || isNaN(contentId)) {
                setError("Параметр content_id відсутній або невірний");
                setIsLoading(false);
                return;
            }

            try {
                const data: InitResponse = await checkAccessWithResponse(contentId, tgId);

                if (!data.ok) {
                    setError(data.error || null);
                    setIsLoading(false);
                    return;
                }

                setContent(data.data || null);
                setIsLoading(false);
            } catch (err: unknown) {
                if (err instanceof Error) {
                    setError(err.message || "Невідома помилка");
                } else {
                    setError("Невідома помилка");
                }
                setIsLoading(false);
            }
        };

        initRequest().catch(err => {
            console.error('[KnowledgeContentPage] Unhandled error in initRequest:', err);
        });
    }, [initData, contentId, tg, tgId]);


    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h4" component="h1" gutterBottom color="primary" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <MenuBookIcon sx={{ mr: 1, fontSize: 32 }} /> База Знань
                </Typography>
            </Box>

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', my: 8 }}>
                    <CircularProgress size={60} thickness={4} />
                    <Typography variant="h6" sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon sx={{ mr: 1 }} /> Завантаження...
                    </Typography>
                </Box>
            ) : (
                <>
                    {!error && telegramUser && (
                        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'primary.light', color: 'white', borderRadius: 2 }}>
                            {(() => {
                                return null;
                            })()}
                            <Typography variant="h6">
                                Вітаємо, {telegramUser.first_name} {telegramUser.last_name || ''}!
                            </Typography>
                        </Paper>
                    )}

                    {error && (
                        <Alert
                            severity="error"
                            variant="filled"
                            sx={{ mb: 3, borderRadius: 2 }}
                        >
                            {(() => {
                                return null;
                            })()}
                            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                                <ErrorIcon sx={{ mr: 1 }} /> Доступ заборонено: {error}
                            </Typography>
                        </Alert>
                    )}

                    {content && (
                        <Card elevation={3} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                            {(() => {
                                return null;
                            })()}
                            <CardHeader
                                avatar={
                                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                                        <MenuBookIcon />
                                    </Avatar>
                                }
                                title={
                                    <Typography variant="h5" component="h2" color="primary">
                                        {content.title}
                                    </Typography>
                                }
                                sx={{ pb: 0 }}
                            />
                            <CardContent>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ px: 1 }}>
                                    {content.content.split('\n').map((paragraph, index) => (
                                        <Typography key={index} variant="body1" paragraph>
                                            {paragraph}
                                        </Typography>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {!content && !isLoading && !error && (
                        <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
                            {(() => {
                                return null;
                            })()}
                            <CardHeader
                                avatar={
                                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                                        <ErrorIcon />
                                    </Avatar>
                                }
                                title={
                                    <Typography variant="h5" component="h2" color="warning.dark">
                                        Контент не знайдено
                                    </Typography>
                                }
                            />
                            <CardContent>
                                <Typography variant="body1">
                                    Запитаний контент не знайдено або у вас немає до нього доступу.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </Container>
    );

};
